import { useState, useCallback, useRef, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface BotProtectionOptions {
  enableRateLimit?: boolean;
  enableHoneypot?: boolean;
  enableDwellTime?: boolean;
  minDwellTime?: number;
}

interface SubmissionData {
  type: 'contact' | 'student' | 'committee';
  [key: string]: any;
}

interface BotProtectionState {
  isProtected: boolean;
  isSubmitting: boolean;
  honeypotValue: string;
  formStartTime: number;
  dwellTime: number;
}

export const useBotProtection = (options: BotProtectionOptions = {}) => {
  const {
    enableRateLimit = true,
    enableHoneypot = true,
    enableDwellTime = true,
    minDwellTime = 1000,
  } = options;

  const { toast } = useToast();
  const formStartTimeRef = useRef<number>(Date.now());
  const [csrfToken] = useState(() => crypto.randomUUID());
  
  const [state, setState] = useState<BotProtectionState>({
    isProtected: false,
    isSubmitting: false,
    honeypotValue: '',
    formStartTime: formStartTimeRef.current,
    dwellTime: 0,
  });

  // Update dwell time periodically
  useEffect(() => {
    if (!enableDwellTime) return;

    const interval = setInterval(() => {
      setState(prev => ({
        ...prev,
        dwellTime: Date.now() - formStartTimeRef.current,
      }));
    }, 1000);

    return () => clearInterval(interval);
  }, [enableDwellTime]);


  const handleHoneypotChange = useCallback((value: string) => {
    setState(prev => ({
      ...prev,
      honeypotValue: value,
    }));
  }, []);

  const validateSubmission = useCallback((data: SubmissionData): string | null => {
    // Check honeypot
    if (enableHoneypot && state.honeypotValue.trim() !== '') {
      console.log('Honeypot triggered');
      return 'Spam detected';
    }

    // Check dwell time
    if (enableDwellTime && state.dwellTime < minDwellTime) {
      console.log(`Insufficient dwell time: ${state.dwellTime}ms`);
      return 'Vérification requise. Merci de réessayer.';
    }


    return null;
  }, [enableHoneypot, enableDwellTime, state.honeypotValue, state.dwellTime, minDwellTime]);

  const submitForm = useCallback(async (data: SubmissionData): Promise<{ success: boolean; message?: string; error?: string }> => {
    // Validate before submission
    const validationError = validateSubmission(data);
    if (validationError) {
      return { success: false, error: validationError };
    }

    setState(prev => ({ ...prev, isSubmitting: true }));

    try {
      // Prepare submission data
      const submissionData = {
        ...data,
        csrfToken,
        honeypot: state.honeypotValue,
        dwellTime: state.dwellTime,
        formStartTime: state.formStartTime,
      };

      // Submit via Supabase edge function
      const { data: result, error } = await supabase.functions.invoke('submit-form', {
        body: submissionData,
      });

      if (error) {
        console.error('Submission error:', error);
        
        // Handle specific error types
        if (error.message?.includes('429')) {
          return { 
            success: false, 
            error: 'Trop de tentatives. Veuillez réessayer plus tard.' 
          };
        }
        
        return { 
          success: false, 
          error: error.message || 'Une erreur est survenue lors de l\'envoi.' 
        };
      }

      if (!result?.success) {
        return { 
          success: false, 
          error: result?.error || 'Erreur lors de l\'envoi du formulaire' 
        };
      }

      // Reset protection state on success
      setState(prev => ({
        ...prev,
        isProtected: false,
        honeypotValue: '',
      }));

      return { 
        success: true, 
        message: result.message || 'Formulaire envoyé avec succès!' 
      };

    } catch (error: any) {
      console.error('Submission error:', error);
      return { 
        success: false, 
        error: 'Une erreur réseau est survenue. Veuillez réessayer.' 
      };
    } finally {
      setState(prev => ({ ...prev, isSubmitting: false }));
    }
  }, [validateSubmission, csrfToken, state]);

  const resetProtection = useCallback(() => {
    setState(prev => ({
      ...prev,
      isProtected: false,
      honeypotValue: '',
    }));
    formStartTimeRef.current = Date.now();
  }, []);

  return {
    // State
    isProtected: state.isProtected,
    isSubmitting: state.isSubmitting,
    dwellTime: state.dwellTime,

    // Handlers
    handleHoneypotChange,

    // Methods
    submitForm,
    resetProtection,

    // Tokens
    csrfToken,
    honeypotValue: state.honeypotValue,

    // Config
    enableHoneypot,
    enableDwellTime,
  };
};

export default useBotProtection;